import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import {
  Upload,
  Image as ImageIcon,
  FileText,
  Plus,
  Loader2,
  Check,
  Folder,
} from "lucide-react";
import { Button } from "@b2b/ui-components";

interface MediaItem {
  id: string;
  url: string;
  thumbnailUrl?: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

interface MediaFolder {
  name: string;
  slug: string;
  count: number;
}

export type MediaSelectorType = "image" | "pdf";

interface MediaSelectorProps {
  selectedMedia: Array<{
    id: string;
    url: string;
    thumbnailUrl?: string;
    originalName: string;
  }>;
  onMediaChange: (
    mediaItems: Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
      originalName: string;
    }>,
    shouldCloseModal?: boolean
  ) => void;
  maxSelection?: number;
  /** Max file size in bytes (e.g. 2*1024*1024 for 2MB). When set, uploads over this size are rejected. */
  maxFileSizeBytes?: number;
  className?: string;
  sellerId?: string;
  /** When true, uses admin media APIs (with optional sellerId). Otherwise seller APIs. */
  isAdminMode?: boolean;
  /** Restrict library listing and uploads to images or PDFs only. */
  mediaType?: MediaSelectorType;
  /** When set, Cancel discards changes (e.g. product dialog restores previous media). Omit for gallery pickers that only use Add Files. */
  onCancel?: () => void;
  /**
   * When true, grid picks and uploads only update internal draft until the user clicks "Add Files".
   * Use for product gallery so the parent list is not updated until confirm.
   */
  deferCommitUntilAdd?: boolean;
  /** When using deferCommitUntilAdd, pass dialog open state so draft resets from parent when the modal closes. */
  modalOpen?: boolean;
}

type SelectedMediaEntry = {
  id: string;
  url: string;
  thumbnailUrl?: string;
  originalName: string;
};

const PAGE_SIZE = 20;
const DEFAULT_FOLDER = "uploads";
const MEDIA_LIBRARY_ONLY = true;

const IMAGE_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.jpg,.jpeg,.png,.webp,.gif,.svg";
const PDF_ACCEPT = "application/pdf,.pdf";

const isImageMime = (mime: string, name?: string) => {
  if (mime?.startsWith("image/")) return true;
  const ext = name?.split(".").pop()?.toLowerCase() || "";
  return ["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext);
};

const isPdfMime = (mime: string, name?: string) =>
  mime === "application/pdf" || !!name?.toLowerCase().endsWith(".pdf");

const matchesMediaType = (
  mime: string,
  name: string | undefined,
  mediaType: MediaSelectorType
) =>
  mediaType === "image" ? isImageMime(mime, name) : isPdfMime(mime, name);

const isUploadFileAllowed = (file: File, mediaType: MediaSelectorType) => {
  const mime = file.type?.toLowerCase() || "";
  return matchesMediaType(mime, file.name, mediaType);
};

export const MediaSelector: React.FC<MediaSelectorProps> = ({
  selectedMedia,
  onMediaChange,
  maxSelection = 10,
  maxFileSizeBytes,
  className = "",
  sellerId,
  isAdminMode = true,
  mediaType = "image",
  onCancel,
  deferCommitUntilAdd = false,
  modalOpen,
}) => {
  const mediaService = isAdminMode ? adminService : sellerService;

  const [mediaFiles, setMediaFiles] = useState<MediaItem[]>([]);
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "upload">("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadingRef = useRef(false);
  const prevSellerRef = useRef(sellerId);
  const prevMediaTypeRef = useRef(mediaType);
  const [draftSelection, setDraftSelection] = useState<SelectedMediaEntry[]>([]);
  const draftSelectionRef = useRef<SelectedMediaEntry[]>([]);

  const uploadAccept = mediaType === "pdf" ? PDF_ACCEPT : IMAGE_ACCEPT;
  const uploadHint =
    mediaType === "pdf"
      ? "PDF files only, up to 50MB each"
      : "Images only (JPEG, PNG, WebP, GIF, SVG), up to 50MB each";

  const selectedIdsKey = useMemo(
    () => selectedMedia.map((m) => m.id).join("|"),
    [selectedMedia]
  );

  // Re-sync draft when the dialog opens/closes or committed selection IDs change.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedMedia read from latest render when modalOpen/selectedIdsKey change
  useEffect(() => {
    if (!deferCommitUntilAdd) return;
    const next = selectedMedia.map((x) => ({ ...x }));
    setDraftSelection(next);
    draftSelectionRef.current = next;
  }, [deferCommitUntilAdd, modalOpen, selectedIdsKey]);

  useEffect(() => {
    if (deferCommitUntilAdd) {
      draftSelectionRef.current = draftSelection;
    }
  }, [deferCommitUntilAdd, draftSelection]);

  const workingSelection: SelectedMediaEntry[] = deferCommitUntilAdd
    ? draftSelection
    : selectedMedia;

  const parseListResponse = (res: unknown) => {
    if (!res || typeof res !== "object") {
      return { mediaData: [] as MediaItem[], totalCount: 0 };
    }
    if (Array.isArray((res as { data?: unknown }).data)) {
      return {
        mediaData: (res as { data: MediaItem[] }).data,
        totalCount: (res as { total?: number }).total || 0,
      };
    }
    const nested = (res as { data?: { data?: MediaItem[]; total?: number } }).data;
    if (nested && Array.isArray(nested.data)) {
      return {
        mediaData: nested.data,
        totalCount: nested.total || 0,
      };
    }
    return { mediaData: [] as MediaItem[], totalCount: 0 };
  };

  const loadFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const list = isAdminMode
        ? await adminService.listMediaFolders(sellerId, MEDIA_LIBRARY_ONLY)
        : await sellerService.listMediaFolders(MEDIA_LIBRARY_ONLY);
      setFolders(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading media folders:", error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [isAdminMode, sellerId]);

  const loadMedia = useCallback(
    async (
      pageNum: number = 1,
      folderSlug: string | null = selectedFolder
    ) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      try {
        const res = isAdminMode
          ? await adminService.listMedia(
              pageNum,
              PAGE_SIZE,
              sellerId,
              undefined,
              folderSlug || undefined,
              MEDIA_LIBRARY_ONLY,
              mediaType
            )
          : await sellerService.listMedia(
              pageNum,
              PAGE_SIZE,
              undefined,
              folderSlug || undefined,
              MEDIA_LIBRARY_ONLY,
              mediaType
            );

        const { mediaData, totalCount } = parseListResponse(res);
        setMediaFiles(mediaData);
        setTotal(totalCount);
      } catch (error) {
        console.error("Error loading media:", error);
        setMediaFiles([]);
        setTotal(0);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [isAdminMode, mediaType, selectedFolder, sellerId]
  );

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    if (sellerId !== undefined) {
      setPage(1);
      setMediaFiles([]);
      setTotal(0);
      setSelectedFolder(null);
    }
  }, [sellerId]);

  useEffect(() => {
    if (prevMediaTypeRef.current !== mediaType) {
      prevMediaTypeRef.current = mediaType;
      setPage(1);
      setActiveTab("all");
    }
  }, [mediaType]);

  useEffect(() => {
    const sellerChanged = prevSellerRef.current !== sellerId;
    prevSellerRef.current = sellerId;
    const pageToFetch = sellerChanged ? 1 : page;
    if (sellerChanged && page !== 1) {
      setPage(1);
    }
    loadMedia(pageToFetch, selectedFolder);
  }, [loadMedia, page, sellerId, selectedFolder]);

  const handleFolderSelect = (slug: string | null) => {
    setSelectedFolder(slug);
    setPage(1);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadError(null);
    setUploading(true);
    try {
      const maxMB = maxFileSizeBytes
        ? (maxFileSizeBytes / (1024 * 1024)).toFixed(1)
        : null;
      const baseSelection = deferCommitUntilAdd
        ? [...draftSelectionRef.current]
        : [...selectedMedia];
      let nextSelection = baseSelection;
      let uploadedCount = 0;
      let skippedCount = 0;
      let notAddedDueToLimit = 0;
      const uploadFolder = selectedFolder || DEFAULT_FOLDER;

      for (const file of Array.from(files)) {
        if (!isUploadFileAllowed(file, mediaType)) {
          skippedCount += 1;
          continue;
        }
        if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
          setUploadError(
            `"${file.name}" exceeds ${maxMB} MB limit. Please choose a smaller file.`
          );
          continue;
        }
        setUploadError(null);
        const result = await mediaService.uploadMedia(
          file,
          uploadFolder,
          MEDIA_LIBRARY_ONLY
        );
        uploadedCount += 1;
        const item = {
          id: result.id,
          url: result.url,
          thumbnailUrl: result.thumbnailUrl,
          originalName: file.name,
        };
        if (nextSelection.length >= maxSelection) {
          notAddedDueToLimit += 1;
          continue;
        }
        if (!nextSelection.some((x) => x.id === item.id)) {
          nextSelection.push(item);
        }
      }

      if (deferCommitUntilAdd) {
        draftSelectionRef.current = nextSelection;
        setDraftSelection(nextSelection);
      } else {
        onMediaChange(nextSelection);
      }

      if (skippedCount > 0) {
        setUploadError(
          mediaType === "pdf"
            ? `${skippedCount} file${skippedCount > 1 ? "s were" : " was"} skipped. Only PDF files are allowed.`
            : `${skippedCount} file${skippedCount > 1 ? "s were" : " was"} skipped. Only image files are allowed.`
        );
      }

      if (notAddedDueToLimit > 0) {
        setUploadError(
          `All files were saved to your library. ${notAddedDueToLimit} could not be added here (limit ${maxSelection}). Open “Select File”, or remove a selection, then add them.`
        );
      }

      await Promise.all([loadMedia(page, selectedFolder), loadFolders()]);
      if (uploadedCount > 0) {
        setActiveTab("all");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError(
        error instanceof Error ? error.message : "Upload failed. Please try again."
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleMediaSelect = (mediaId: string) => {
    const mediaItem = mediaFiles.find((item) => item.id === mediaId);
    if (!mediaItem) return;

    if (!matchesMediaType(mediaItem.mimeType, mediaItem.originalName, mediaType)) {
      return;
    }

    const isSelected = workingSelection.some((item) => item.id === mediaId);

    if (deferCommitUntilAdd) {
      if (isSelected) {
        const next = workingSelection.filter((item) => item.id !== mediaId);
        draftSelectionRef.current = next;
        setDraftSelection(next);
      } else if (workingSelection.length < maxSelection) {
        const next = [
          ...workingSelection,
          {
            id: mediaItem.id,
            url: mediaItem.url,
            thumbnailUrl: mediaItem.thumbnailUrl,
            originalName: mediaItem.originalName,
          },
        ];
        draftSelectionRef.current = next;
        setDraftSelection(next);
      }
      return;
    }

    if (isSelected) {
      onMediaChange(selectedMedia.filter((item) => item.id !== mediaId));
    } else if (selectedMedia.length < maxSelection) {
      onMediaChange([
        ...selectedMedia,
        {
          id: mediaItem.id,
          url: mediaItem.url,
          thumbnailUrl: mediaItem.thumbnailUrl,
          originalName: mediaItem.originalName,
        },
      ]);
    }
  };

  const fileSize = (bytes: number) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const selectedMediaItems = workingSelection;
  const allMediaCount = folders.reduce((sum, folder) => sum + folder.count, 0);
  const activeFolderLabel =
    selectedFolder === null
      ? "All folders"
      : folders.find((folder) => folder.slug === selectedFolder)?.name ||
        "Selected folder";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={`space-y-4 bg-white ${className}`}>
      <div className="bg-white">
        <div className="flex space-x-0">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "all"
                ? "border-orange-500 text-orange-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("all")}
          >
            Select File
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "upload"
                ? "border-orange-500 text-orange-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("upload")}
          >
            Upload New
          </button>
        </div>
      </div>

      {uploadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
          {uploadError}
        </div>
      )}

      {activeTab === "upload" && (
        <div className="border border-gray-200 rounded-lg bg-white p-6 shadow-sm">
          <div className="border-2 border-dashed border-orange-300 rounded-lg p-8 text-center bg-orange-50">
            <Upload className="w-12 h-12 mx-auto mb-4 text-orange-400" />
            <p className="text-sm text-gray-600 mb-1">Upload new {mediaType === "pdf" ? "PDF" : "image"} files</p>
            <p className="text-xs text-gray-500 mb-4">
              Uploading to: <span className="font-medium">{activeFolderLabel}</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple={maxSelection > 1}
              accept={uploadAccept}
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="primary"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Select Files
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2">{uploadHint}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {selectedMediaItems.length} File
            {selectedMediaItems.length !== 1 ? "s" : ""} selected
          </span>
          {selectedMediaItems.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (deferCommitUntilAdd) {
                  draftSelectionRef.current = [];
                  setDraftSelection([]);
                } else {
                  onMediaChange([]);
                }
              }}
              className="text-xs"
            >
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2 flex-wrap gap-2 justify-end">
          {total > PAGE_SIZE && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </Button>
              <span className="text-xs text-gray-500">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </>
          )}

          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Button
            variant="primary"
            disabled={selectedMediaItems.length === 0}
            onClick={() => {
              const toCommit = deferCommitUntilAdd
                ? [...draftSelectionRef.current]
                : [...selectedMedia];
              onMediaChange(toCommit, true);
            }}
          >
            Add Files
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)] gap-4">
        <aside className="border rounded-lg p-3 bg-white min-w-0 flex flex-col lg:max-h-96">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 shrink-0">Folders</h3>
          {foldersLoading ? (
            <p className="text-xs text-gray-500 py-2">Loading folders...</p>
          ) : (
            <div className="space-y-1 min-w-0 min-h-0 max-h-52 sm:max-h-64 lg:max-h-[calc(24rem-2.75rem)] overflow-y-auto overscroll-y-contain pr-0.5">
              <button
                type="button"
                onClick={() => handleFolderSelect(null)}
                className={`w-full min-w-0 flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm transition-colors ${
                  selectedFolder === null
                    ? "bg-orange-50 text-orange-700 border border-orange-200"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <span className="inline-flex min-w-0 flex-1 items-center gap-2">
                  <Folder className="w-4 h-4 shrink-0" />
                  <span className="truncate">All folders</span>
                </span>
                <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                  {allMediaCount}
                </span>
              </button>
              {folders.map((folder) => (
                <button
                  key={folder.slug}
                  type="button"
                  onClick={() => handleFolderSelect(folder.slug)}
                  className={`w-full min-w-0 flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm transition-colors ${
                    selectedFolder === folder.slug
                      ? "bg-orange-50 text-orange-700 border border-orange-200"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <span className="inline-flex min-w-0 flex-1 items-center gap-2">
                    <Folder className="w-4 h-4 shrink-0" />
                    <span className="truncate" title={folder.name}>
                      {folder.name}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                    {folder.count}
                  </span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <div className="border border-gray-200 rounded-lg bg-white shadow-sm min-w-0">
          <div className="p-4">
            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">Loading media...</p>
              </div>
            ) : mediaFiles.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                {mediaType === "pdf" ? (
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                ) : (
                  <ImageIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                )}
                <p className="text-sm">
                  No {mediaType === "pdf" ? "PDF files" : "images"} found
                  {selectedFolder ? " in this folder" : ""}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 max-h-96 overflow-y-auto">
                {mediaFiles.map((item) => {
                  const isSelected = workingSelection.some(
                    (selected) => selected.id === item.id
                  );
                  const canSelect =
                    workingSelection.length < maxSelection || isSelected;
                  const showAsImage = isImageMime(item.mimeType, item.originalName);
                  const showAsPdf = isPdfMime(item.mimeType, item.originalName);

                  return (
                    <div
                      key={item.id}
                      className={`relative group cursor-pointer rounded-lg border-2 transition-all ${
                        isSelected
                          ? "border-orange-500 bg-orange-50 shadow-md"
                          : canSelect
                            ? "border-gray-200 hover:border-orange-300 hover:shadow-sm"
                            : "border-gray-100 opacity-50 cursor-not-allowed"
                      }`}
                      onClick={() => canSelect && handleMediaSelect(item.id)}
                    >
                      <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                        {showAsImage ? (
                          <img
                            src={item.thumbnailUrl || item.url}
                            alt={item.originalName}
                            className="w-full h-full object-cover"
                          />
                        ) : showAsPdf ? (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-500 px-2">
                            <FileText className="w-8 h-8" />
                            <span className="text-[10px] uppercase tracking-wide">PDF</span>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-orange-500 text-white rounded-full p-1 shadow-lg">
                          <Check className="w-3 h-3" />
                        </div>
                      )}

                      {!canSelect && !isSelected && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded">
                          <span className="text-white text-xs font-medium">
                            Max {maxSelection}
                          </span>
                        </div>
                      )}

                      <div className="p-2">
                        <p
                          className="text-xs text-gray-600 truncate font-medium"
                          title={item.originalName}
                        >
                          {item.originalName}
                        </p>
                        <p className="text-xs text-gray-400">{fileSize(item.size)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaSelector;
